// Le récap des marges (migration 29) : lignes par chantier et par mois, et le
// détail de leurs achats. Réservé aux administrateurs (RLS admin_only).

import { tableAbsente } from '@imp/shared/erreurs-supabase';
import {
  type AchatRecap,
  type FeuilleExport,
  type FeuilleXls,
  type LignePreRemplie,
  type LigneRecap,
  type LigneReprise,
  type VenteDuMois,
} from '@imp/shared/recap-marges';

import { supabase } from '@/lib/supabase';
import { contactDisplayName, listContacts, ORG_ID } from '@/modules/clients/api';
import { listVentesExternes } from '@/modules/pilotage/tresorerie-api';

/** Ce que l'on dit tant que la migration 29 n'est pas passée. */
export const BASE_EN_ATTENTE_MARGES =
  'Mise à jour de la base en attente : le récap des marges a besoin de la migration 29.';

function erreur(message: string): Error {
  if (tableAbsente(message, 'marges_lignes') || tableAbsente(message, 'marges_achats'))
    return new Error(BASE_EN_ATTENTE_MARGES);
  return new Error(message);
}

export interface LigneMargeStockee extends LigneRecap {
  ordre: number;
  source: 'crm' | 'excel';
}

interface LigneBrute {
  id: string;
  mois: number;
  libelle: string;
  chantier_id: string | null;
  facture_ht: number | string | null;
  jours: number | string | null;
  equipe: number | string | null;
  heures_jour: number | string | null;
  precisions: string;
  achats_connus: boolean;
  ordre: number;
  source: string;
  marges_achats: { fournisseur: string; montant: number | string; ordre: number }[];
}

const nombre = (v: number | string | null): number | null =>
  v === null || v === '' ? null : Number.isFinite(Number(v)) ? Number(v) : null;

export async function listLignesMarges(annee: number): Promise<LigneMargeStockee[]> {
  const { data, error } = await supabase
    .from('marges_lignes')
    .select(
      'id,mois,libelle,chantier_id,facture_ht,jours,equipe,heures_jour,precisions,achats_connus,ordre,source,marges_achats(fournisseur,montant,ordre)',
    )
    .eq('organization_id', ORG_ID)
    .eq('annee', annee)
    .is('deleted_at', null)
    .order('mois')
    .order('ordre')
    .order('id')
    .overrideTypes<LigneBrute[], { merge: false }>();
  if (error) throw erreur(error.message);
  return data.map((l) => ({
    id: l.id,
    mois: l.mois,
    libelle: l.libelle,
    chantierId: l.chantier_id,
    factureHt: nombre(l.facture_ht),
    jours: nombre(l.jours),
    equipe: nombre(l.equipe),
    heuresJour: nombre(l.heures_jour),
    precisions: l.precisions,
    achatsConnus: l.achats_connus,
    achats: [...l.marges_achats]
      .sort((a, b) => a.ordre - b.ordre)
      .map((a) => ({ fournisseur: a.fournisseur, montant: Number(a.montant) })),
    ordre: l.ordre,
    source: l.source === 'excel' ? 'excel' : 'crm',
  }));
}

export interface ChampsLigne {
  libelle?: string;
  chantier_id?: string | null;
  facture_ht?: number | null;
  jours?: number | null;
  equipe?: number | null;
  heures_jour?: number | null;
  precisions?: string;
}

export async function creerLigneMarge(
  annee: number,
  mois: number,
  ordre: number,
  champs: ChampsLigne = {},
): Promise<void> {
  const { error } = await supabase.from('marges_lignes').insert({
    organization_id: ORG_ID,
    annee,
    mois,
    ordre,
    source: 'crm',
    ...champs,
  });
  if (error) throw erreur(error.message);
}

export async function modifierLigneMarge(id: string, champs: ChampsLigne): Promise<void> {
  const { error } = await supabase.from('marges_lignes').update(champs).eq('id', id);
  if (error) throw erreur(error.message);
}

/** Suppression douce : la ligne reste en base, hors du récap. */
export async function supprimerLigneMarge(id: string): Promise<void> {
  const { error } = await supabase
    .from('marges_lignes')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw erreur(error.message);
}

/**
 * Réécrit les achats d'une ligne : le détail entier, et s'ils sont connus.
 * Des achats « connus » et vides valent 0 € ; « inconnus », ils retirent la
 * ligne de la marge.
 */
export async function enregistrerAchats(
  ligneId: string,
  connus: boolean,
  achats: readonly AchatRecap[],
): Promise<void> {
  const { error: e1 } = await supabase.from('marges_achats').delete().eq('ligne_id', ligneId);
  if (e1) throw erreur(e1.message);
  const lignes = connus ? achats.filter((a) => Number.isFinite(a.montant)) : [];
  if (lignes.length > 0) {
    const { error: e2 } = await supabase.from('marges_achats').insert(
      lignes.map((a, i) => ({
        organization_id: ORG_ID,
        ligne_id: ligneId,
        fournisseur: a.fournisseur.trim(),
        montant: a.montant,
        ordre: i,
      })),
    );
    if (e2) throw erreur(e2.message);
  }
  const { error: e3 } = await supabase
    .from('marges_lignes')
    .update({ achats_connus: connus })
    .eq('id', ligneId);
  if (e3) throw erreur(e3.message);
}

/**
 * Reprend les lignes du tableur pour une année.
 *
 * `remplacer` retire d'abord les lignes de cette année déjà reprises du
 * tableur (jamais celles saisies dans le CRM) : réimporter le même fichier ne
 * double rien.
 */
export async function reprendreTableur(
  annee: number,
  lignes: readonly LigneReprise[],
  remplacer: boolean,
): Promise<number> {
  if (remplacer) {
    const { error } = await supabase
      .from('marges_lignes')
      .update({ deleted_at: new Date().toISOString() })
      .eq('organization_id', ORG_ID)
      .eq('annee', annee)
      .eq('source', 'excel')
      .is('deleted_at', null);
    if (error) throw erreur(error.message);
  }
  const { data, error } = await supabase
    .from('marges_lignes')
    .insert(
      lignes.map((l, i) => ({
        organization_id: ORG_ID,
        annee,
        mois: l.mois,
        libelle: l.libelle,
        facture_ht: l.factureHt,
        jours: l.jours,
        equipe: l.equipe,
        heures_jour: l.heuresJour,
        precisions: l.precisions,
        achats_connus: l.achats !== null,
        ordre: (i + 1) * 10,
        source: 'excel',
      })),
    )
    .select('id,ordre')
    .overrideTypes<{ id: string; ordre: number }[], { merge: false }>();
  if (error) throw erreur(error.message);
  const idParOrdre = new Map(data.map((d) => [d.ordre, d.id]));
  const achats = lignes.flatMap((l, i) => {
    const ligneId = idParOrdre.get((i + 1) * 10);
    return ligneId === undefined || l.achats === null
      ? []
      : l.achats.map((montant, k) => ({
          organization_id: ORG_ID,
          ligne_id: ligneId,
          fournisseur: '',
          montant,
          ordre: k,
        }));
  });
  if (achats.length > 0) {
    const { error: e2 } = await supabase.from('marges_achats').insert(achats);
    if (e2) throw erreur(e2.message);
  }
  return lignes.length;
}

/** Le classeur, feuille par feuille, AVEC ses formules : les achats y sont détaillés. */
export async function lireClasseurRecap(file: File): Promise<FeuilleXls[]> {
  const XLSX = await import('xlsx');
  const wb = XLSX.read(await file.arrayBuffer(), { cellFormula: true });
  return wb.SheetNames.map((nom) => {
    const ws = wb.Sheets[nom];
    if (ws === undefined) return { nom, lignes: [] };
    const ref = XLSX.utils.decode_range(ws['!ref'] ?? 'A1:A1');
    const lignes = [];
    for (let r = 0; r <= ref.e.r; r++) {
      const ligne = [];
      for (let c = 0; c <= ref.e.c; c++) {
        const cell = ws[XLSX.utils.encode_cell({ r, c })] as
          | { v?: unknown; f?: string }
          | undefined;
        ligne.push(cell === undefined ? null : { v: cell.v, f: cell.f });
      }
      lignes.push(ligne);
    }
    return { nom, lignes };
  });
}

/** Crée d'un coup les lignes d'un mois, à la suite de celles qui existent. */
export async function creerLignesMarge(
  annee: number,
  mois: number,
  premierOrdre: number,
  lignes: readonly LignePreRemplie[],
): Promise<void> {
  if (lignes.length === 0) return;
  const { error } = await supabase.from('marges_lignes').insert(
    lignes.map((l, i) => ({
      organization_id: ORG_ID,
      annee,
      mois,
      ordre: premierOrdre + i * 10,
      source: 'crm',
      libelle: l.libelle,
      chantier_id: l.chantierId,
      facture_ht: l.factureHt,
      precisions: l.precisions,
    })),
  );
  if (error) throw erreur(error.message);
}

/**
 * Les ventes de l'année, mois par mois (index 0 = janvier), comme le tableau
 * des ventes les compte : les pièces Extrabat et les factures émises du CRM.
 * Les factures du CRM portent leur chantier ; les pièces Extrabat, non.
 */
export async function listVentesPourRecap(annee: number): Promise<VenteDuMois[][]> {
  const [externes, crmR, contacts] = await Promise.all([
    listVentesExternes(annee),
    supabase
      .from('factures')
      .select('numero,kind,date_emission,montant_ht,chantier_id,contact_id')
      .neq('statut', 'brouillon')
      .is('deleted_at', null)
      .gte('date_emission', `${String(annee)}-01-01`)
      .lte('date_emission', `${String(annee)}-12-31`)
      .overrideTypes<
        {
          numero: string | null;
          kind: string;
          date_emission: string | null;
          montant_ht: number | string;
          chantier_id: string | null;
          contact_id: string | null;
        }[],
        { merge: false }
      >(),
    listContacts(),
  ]);
  if (crmR.error) throw new Error(crmR.error.message);
  const nomDe = new Map(contacts.map((c) => [c.id, contactDisplayName(c)]));
  const parMois: VenteDuMois[][] = Array.from({ length: 12 }, () => []);
  for (const v of externes) {
    const i = Number(v.date_emission.slice(5, 7)) - 1;
    parMois[i]?.push({
      numero: v.numero,
      type: v.type,
      ht: Number(v.base_55) + Number(v.base_10) + Number(v.base_20) + Number(v.base_exo),
      chantierId: null,
      nom: v.numero,
    });
  }
  for (const f of crmR.data) {
    if (f.date_emission === null) continue;
    const i = Number(f.date_emission.slice(5, 7)) - 1;
    const nom = (f.contact_id === null ? undefined : nomDe.get(f.contact_id)) ?? f.numero ?? 'facture';
    parMois[i]?.push({
      numero: f.numero ?? '',
      type: f.kind === 'avoir' ? 'avoir' : 'facture',
      ht: Math.abs(Number(f.montant_ht)),
      chantierId: f.chantier_id,
      nom,
    });
  }
  return parMois;
}

/**
 * Télécharge le récap au format du tableur : `recap-marges-AAAA.xlsx`, un
 * onglet par mois et le récap annuel. Les achats y sont en formule, pour que le
 * fichier se reprenne tel quel.
 */
export async function exporterRecapMarges(annee: number, feuilles: readonly FeuilleExport[]): Promise<void> {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  for (const feuille of feuilles) {
    const ws: Record<string, unknown> = {};
    let maxCol = 0;
    feuille.lignes.forEach((ligne, r) => {
      ligne.forEach((c, k) => {
        if (c.v === null && c.f === undefined) return;
        maxCol = Math.max(maxCol, k);
        ws[XLSX.utils.encode_cell({ r, c: k })] = {
          t: typeof c.v === 'number' ? 'n' : 's',
          v: c.v ?? '',
          ...(c.f !== undefined ? { f: c.f } : {}),
          ...(c.z !== undefined ? { z: c.z } : {}),
        };
      });
    });
    ws['!ref'] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: Math.max(0, feuille.lignes.length - 1), c: maxCol },
    });
    ws['!cols'] = [{ wch: 28 }, { wch: 30 }, ...Array.from({ length: 8 }, () => ({ wch: 13 })), { wch: 40 }];
    XLSX.utils.book_append_sheet(wb, ws, feuille.nom);
  }
  XLSX.writeFile(wb, `recap-marges-${String(annee)}.xlsx`);
}
